/**
 * EmployeeSearchBar Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 * The parent owns the filter state (via `filters`/`onFiltersChange`), so this
 * component reads/writes it directly instead of using useFilterState — that keeps
 * the company→department→occupation cascade consistent with external changes.
 */

import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
} from '@mui/material';
import { InlineFilterBar, InlineMultiSelect, InlineSelect } from '../common';
import {
  EmployeeFilters as IEmployeeFilters,
  EmployeeStatus,
  EmployeeGender,
  EMPLOYEE_STATUS_LABELS,
  GENDER_LABELS,
} from '../../types/employee';
import { BRAZILIAN_STATES } from '../../types/common';
import { companyService, departmentService, occupationService } from '../../services';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import type { ActiveChip } from '../../hooks/useFilterState';

interface EmployeeSearchBarProps {
  filters: IEmployeeFilters;
  onFiltersChange: (filters: IEmployeeFilters) => void;
  exporting?: boolean;
  total?: number;
}

interface CompanyOption {
  id: number;
  tradeName: string;
}

interface DepartmentOption {
  id: number;
  name: string;
  companyId: number;
}

interface OccupationOption {
  id: number;
  name: string;
  departmentId: number;
}

const STATUS_OPTIONS = Object.values(EmployeeStatus).map((s) => ({
  value: s,
  label: EMPLOYEE_STATUS_LABELS[s],
}));
const GENDER_OPTIONS = Object.values(EmployeeGender).map((g) => ({
  value: g,
  label: GENDER_LABELS[g],
}));
const STATE_OPTIONS = BRAZILIAN_STATES.map((s) => ({ value: s, label: s }));

export const EmployeeSearchBar: React.FC<EmployeeSearchBarProps> = ({
  filters,
  onFiltersChange,
  exporting = false,
  total,
}) => {
  const { shouldShowCompanyField, effectiveCompanyId } = useCompanyContext();

  // Data for autocomplete
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [allDepartments, setAllDepartments] = useState<DepartmentOption[]>([]);
  const [allOccupations, setAllOccupations] = useState<OccupationOption[]>([]);
  const [loading, setLoading] = useState(false);

  // City is free text: keep a local buffer and commit on blur/Enter so we don't
  // fire a request on every keystroke (the search box already debounces).
  const [cityDraft, setCityDraft] = useState(filters.city ?? '');
  useEffect(() => {
    setCityDraft(filters.city ?? '');
  }, [filters.city]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [companiesRes, departmentsRes, occupationsRes] = await Promise.all([
          companyService.getCompanies({ page: 1, limit: 1000 }),
          departmentService.getDepartments({ page: 1, limit: 1000 }),
          occupationService.getOccupations({ page: 1, limit: 1000 }),
        ]);

        setCompanies(
          companiesRes.data.map((c) => ({
            id: Number(c.id),
            tradeName: c.tradeName || c.legalName,
          }))
        );
        setAllDepartments(
          departmentsRes.data.map((d) => ({
            id: Number(d.id),
            name: d.name,
            companyId: Number(d.companyId),
          }))
        );
        setAllOccupations(
          occupationsRes.data.map((o) => ({
            id: Number(o.id),
            name: o.name,
            departmentId: Number(o.departmentId),
          }))
        );
      } catch (error) {
        console.error('Error loading filter data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Effective company ID for filtering (fixed company when the field is hidden)
  const activeCompanyId =
    !shouldShowCompanyField && effectiveCompanyId ? effectiveCompanyId : filters.companyId;

  const filteredDepartments = activeCompanyId
    ? allDepartments.filter((d) => String(d.companyId) === String(activeCompanyId))
    : allDepartments;

  const filteredOccupations = filters.departmentId
    ? allOccupations.filter((o) => String(o.departmentId) === String(filters.departmentId))
    : allOccupations;

  // Apply live: build the next filters (clearing dependents) and commit immediately.
  const commit = (field: keyof IEmployeeFilters, value: unknown) => {
    const next: IEmployeeFilters = { ...filters, [field]: (value ?? undefined) as never };
    if (field === 'companyId') {
      next.departmentId = undefined;
      next.occupationId = undefined;
    }
    if (field === 'departmentId') {
      next.occupationId = undefined;
    }
    onFiltersChange(next);
  };

  const statusValue: string[] = Array.isArray(filters.status)
    ? filters.status
    : filters.status
    ? [filters.status]
    : [];

  const genderValue = Array.isArray(filters.gender)
    ? filters.gender[0] ?? ''
    : filters.gender ?? '';

  const stateValue = Array.isArray(filters.state) ? filters.state[0] ?? '' : filters.state ?? '';

  const selectedCompany = companies.find((c) => c.id === Number(filters.companyId)) || null;
  const selectedDepartment =
    filteredDepartments.find((d) => d.id === Number(filters.departmentId)) || null;
  const selectedOccupation =
    filteredOccupations.find((o) => o.id === Number(filters.occupationId)) || null;

  // Active chips (search excluded; hidden company excluded)
  const getFilterLabel = (key: string, value: unknown): string => {
    switch (key) {
      case 'status':
        return Array.isArray(value)
          ? value.map((s) => EMPLOYEE_STATUS_LABELS[s as EmployeeStatus]).join(', ')
          : EMPLOYEE_STATUS_LABELS[value as EmployeeStatus];
      case 'gender':
        return Array.isArray(value)
          ? value.map((g) => GENDER_LABELS[g as EmployeeGender]).join(', ')
          : GENDER_LABELS[value as EmployeeGender];
      case 'companyId':
        return companies.find((c) => c.id === Number(value))?.tradeName ?? String(value);
      case 'departmentId':
        return allDepartments.find((d) => d.id === Number(value))?.name ?? String(value);
      case 'occupationId':
        return allOccupations.find((o) => o.id === Number(value))?.name ?? String(value);
      default:
        return String(value);
    }
  };

  const filterDisplayNames: Record<string, string> = {
    status: 'Status',
    gender: 'Sexo',
    companyId: 'Empresa',
    departmentId: 'Setor',
    occupationId: 'Cargo',
    state: 'Estado',
    city: 'Cidade',
  };

  const removeFilter = (key: keyof IEmployeeFilters) => {
    const next = { ...filters };
    delete next[key];
    if (key === 'companyId') {
      delete next.departmentId;
      delete next.occupationId;
    }
    if (key === 'departmentId') {
      delete next.occupationId;
    }
    onFiltersChange(next);
  };

  const chips: ActiveChip[] = Object.entries(filters)
    .filter(([key, value]) => {
      if (key === 'search') return false;
      if (value === undefined || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      if (key === 'companyId' && !shouldShowCompanyField) return false;
      return true;
    })
    .map(([key, value]) => ({
      key,
      label: filterDisplayNames[key] ?? key,
      value: getFilterLabel(key, value),
      onDelete: () => removeFilter(key as keyof IEmployeeFilters),
    }));

  const clearAll = () => onFiltersChange({ search: filters.search });

  const commitCity = () => {
    const trimmed = cityDraft.trim();
    if ((filters.city ?? '') !== trimmed) {
      onFiltersChange({ ...filters, city: trimmed || undefined });
    }
  };

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar por nome, CPF ou matrícula..."
      searchValue={filters.search ?? ''}
      onSearchChange={(v) => onFiltersChange({ ...filters, search: v || undefined })}
      resultCount={total}
      resultNoun="funcionários"
      chips={chips}
      onClearAll={clearAll}
      loading={exporting}
    >
      <InlineMultiSelect
        label="Status"
        value={statusValue}
        options={STATUS_OPTIONS}
        onChange={(v) => commit('status', v.length > 0 ? (v as EmployeeStatus[]) : undefined)}
        minWidth={140}
      />
      <InlineSelect
        label="Sexo"
        value={genderValue}
        options={GENDER_OPTIONS}
        onChange={(v) => commit('gender', v)}
        emptyLabel="Todos"
        minWidth={120}
      />
      {shouldShowCompanyField && (
        <Autocomplete
          options={companies}
          getOptionLabel={(option) => option.tradeName}
          value={selectedCompany}
          onChange={(_e, newValue) => commit('companyId', newValue?.id)}
          loading={loading}
          size="small"
          sx={{ minWidth: 190 }}
          renderInput={(params) => <TextField {...params} label="Empresa" />}
        />
      )}
      <Autocomplete
        options={filteredDepartments}
        getOptionLabel={(option) => option.name}
        value={selectedDepartment}
        onChange={(_e, newValue) => commit('departmentId', newValue?.id)}
        loading={loading}
        disabled={!activeCompanyId}
        size="small"
        sx={{ minWidth: 170 }}
        renderInput={(params) => <TextField {...params} label="Setor" />}
      />
      <Autocomplete
        options={filteredOccupations}
        getOptionLabel={(option) => option.name}
        value={selectedOccupation}
        onChange={(_e, newValue) => commit('occupationId', newValue?.id)}
        loading={loading}
        disabled={!filters.departmentId}
        size="small"
        sx={{ minWidth: 170 }}
        renderInput={(params) => <TextField {...params} label="Cargo" />}
      />
      <InlineSelect
        label="Estado"
        value={stateValue}
        options={STATE_OPTIONS}
        onChange={(v) => commit('state', v)}
        emptyLabel="Todos"
        minWidth={110}
      />
      <TextField
        label="Cidade"
        value={cityDraft}
        onChange={(e) => setCityDraft(e.target.value)}
        onBlur={commitCity}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commitCity();
        }}
        size="small"
        sx={{ minWidth: 140 }}
      />
    </InlineFilterBar>
  );
};
